import { Injectable } from "@angular/core";
import {
	datadogRum,
	RumInitConfiguration,
	RumEvent,
	RumErrorEvent
} from "@datadog/browser-rum";
import { datadogLogs, LogsInitConfiguration } from "@datadog/browser-logs";
import { environment } from "@env";
import { filter } from "rxjs/operators";
import { AuthService } from "@core/services/auth/auth.service";
import { NavigationEnd, Router } from "@angular/router";

interface Logger {
	debug: (message: string, context?: { [x: string]: unknown }) => void;
	info: (message: string, context?: { [x: string]: unknown }) => void;
	warn: (message: string, context?: { [x: string]: unknown }) => void;
	error: (message: string, context?: { [x: string]: unknown }) => void;
}

interface DatadogLogs {
	logger: Logger;
}

@Injectable({
	providedIn: "root"
})
export class DatadogService {
	private initialized = false;

	constructor(
		private authService: AuthService,
		private router: Router
	) {}

	init() {
		if (!environment?.DATADOG) {
			return;
		}

		const dataDogConfig = this.transformKeys(environment.DATADOG);

		dataDogConfig.beforeSend = (event: RumEvent) => {
			return !this.isExtensionError(event as RumErrorEvent);
		};

		datadogRum.init({
			site: "datadoghq.com",
			service: "base-by-asaas",
			trackViewsManually: true,
			...dataDogConfig
		} as RumInitConfiguration);

		datadogLogs.init({
			site: "datadoghq.com",
			forwardErrorsToLogs: true,
			...dataDogConfig
		} as LogsInitConfiguration);

		this.setUser();
		this.startView(dataDogConfig);
		this.initialized = true;
	}

	private setUser() {
		const user = this.authService.getUserLogin();
		if (user) {
			datadogRum.setUser({
				id: String(user.idUsuario),
				name: user.nomeUsuario,
				email: user.email,
				idUnidadePersistencia: user.idUnidadePersistencia,
				nomeUnidadePersistencia: user.nomeUnidadePersistencia
			});
		}
	}

	private startView(config: Record<string | number, unknown>) {
		this.router.events
			.pipe(
				filter(
					(event): event is NavigationEnd =>
						event instanceof NavigationEnd
				)
			)
			.subscribe((event) => {
				datadogRum.startView({
					name: event.urlAfterRedirects,
					service: String(config.service),
					version: String(config.version)
				});
			});
	}

	private transformKeys(
		obj: Record<string | number, unknown>
	): Record<string | number, unknown> {
		return Object.keys(obj).reduce(
			(result, key) => {
				const newKey = key
					.toLowerCase()
					.replace(/_(.)/g, (_, group1: string) =>
						group1.toUpperCase()
					);
				result[newKey] = obj[key];
				return result;
			},
			{} as Record<string | number, unknown>
		);
	}

	customLog(
		type: "debug" | "info" | "warn" | "error" = "info",
		message: string,
		context?: { [x: string]: unknown }
	): void {
		if (this.initialized) {
			(datadogLogs as DatadogLogs)?.logger?.[type]?.(message, context);
		}
	}

	private isExtensionError(event: RumErrorEvent): boolean {
		if (event?.error?.message || event?.error?.stack) {
			return <boolean>(
				(event.error.message?.includes?.("chrome-extension") ||
					event.error.message?.includes?.("moz-extension") ||
					event.error.stack?.includes?.("chrome-extension") ||
					event.error.stack?.includes?.("moz-extension"))
			);
		}
		return false;
	}
}
